import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Icons,
  iconSize,
  palette,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useSnackbar,
} from '@munifocus/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  detectPasskeySupport,
  isPasskeyCancellation,
  useEnrollPasskey,
  usePasskeys,
  useRevokePasskey,
  type PasskeySummary,
} from '../../lib/webauthn-api';
import { useSubmitValidation } from '../../lib/useSubmitValidation';
import { SettingsSection } from './SettingsSection';
import { frenchFirstOfMonth } from '@munifocus/utils';

/** Format an ISO timestamp in the officer's current language, date only. */
function formatDate(iso: string, locale: string): string {
  return frenchFirstOfMonth(
    new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
  );
}

/** "Last used …" for a passkey that has signed in, "Added …" for one that hasn't. */
function describeUse(
  passkey: PasskeySummary,
  locale: string,
  t: (key: string, vars?: Record<string, string>) => string,
): string {
  return passkey.lastUsedAt
    ? t('gov.settings.passkeys.lastUsed', { date: formatDate(passkey.lastUsedAt, locale) })
    : t('gov.settings.passkeys.neverUsed', { date: formatDate(passkey.createdAt, locale) });
}

/**
 * Passkey enrolment and revocation for the signed-in officer.
 *
 * Renders `null` outright on a device with no platform authenticator — the same
 * rule the Login screen follows. An officer on a desktop that cannot hold a
 * passkey is not shown a section they can only fail at.
 */
export function PasskeysSection() {
  const { t, i18n } = useTranslation();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [label, setLabel] = useState('');
  const v = useSubmitValidation({
    label: label.trim() ? null : t('common.validation.required'),
  });
  // Two separate error slots on purpose. An enrolment failure has to be shown
  // inside the still-open dialog — anything rendered in the section behind it is
  // covered by the modal (and `aria-hidden`), so the officer would never see it.
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<PasskeySummary | null>(null);

  useEffect(() => {
    let active = true;
    detectPasskeySupport()
      .then((value) => {
        if (active) setSupported(value);
      })
      // Detection must never be able to break the rest of the settings page.
      .catch(() => {
        if (active) setSupported(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Only ask the API for credentials once we know the device can use them.
  const { data: passkeys, isLoading } = usePasskeys(supported === true);
  const enroll = useEnrollPasskey();
  const revoke = useRevokePasskey();
  const snackbar = useSnackbar();

  if (supported !== true) return null;

  const onEnroll = async () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setEnrollError(null);
    try {
      await enroll.mutateAsync(trimmed);
      setDialogOpen(false);
      setLabel('');
      snackbar.success(t('gov.settings.passkeys.enrolledToast'));
    } catch (err) {
      // A dismissed OS prompt just leaves the dialog open to try again.
      if (!isPasskeyCancellation(err)) {
        setEnrollError(t('gov.settings.passkeys.enrollError'));
      }
    }
  };

  const onRevoke = async (passkey: PasskeySummary) => {
    setListError(null);
    try {
      await revoke.mutateAsync(passkey.id);
      setPendingRevoke(null);
      snackbar.success(t('gov.settings.passkeys.revokedToast'));
    } catch {
      // Close first, so the message lands in the section rather than behind the
      // confirmation dialog.
      setPendingRevoke(null);
      setListError(t('gov.settings.passkeys.revokeError'));
    }
  };

  return (
    <SettingsSection
      icon={Icons.KeyRound}
      title={t('gov.settings.passkeys.title')}
      description={t('gov.settings.passkeys.description')}
    >
      <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        {listError && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {listError}
          </Alert>
        )}

        {isLoading ? (
          <CircularProgress size={20} />
        ) : passkeys && passkeys.length > 0 ? (
          <Box sx={{ width: '100%' }}>
            {passkeys.map((passkey) => (
              <Stack
                key={passkey.id}
                direction="row"
                spacing={1.5}
                sx={{
                  py: 1.25,
                  alignItems: 'center',
                  borderBottom: `1px solid ${palette.border.line}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Icons.KeyRound size={iconSize.md} color={palette.text.muted} />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{passkey.label}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: palette.text.muted }}>
                    {describeUse(passkey, i18n.language, t)}
                  </Typography>
                </Box>
                <Tooltip title={t('gov.settings.passkeys.revoke')}>
                  <IconButton
                    aria-label={t('gov.settings.passkeys.revokeNamed', { label: passkey.label })}
                    onClick={() => setPendingRevoke(passkey)}
                  >
                    <Icons.Trash2 size={iconSize.md} color={palette.text.muted} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: palette.text.muted }}>
            {t('gov.settings.passkeys.empty')}
          </Typography>
        )}

        <Button
          variant="outlined"
          startIcon={<Icons.Plus size={iconSize.md} />}
          disabled={enroll.isPending}
          onClick={() => {
            setEnrollError(null);
            setListError(null);
            setDialogOpen(true);
          }}
        >
          {t('gov.settings.passkeys.add')}
        </Button>
      </Stack>

      {/* Enrolment: name the device first, so the revocation list is meaningful later. */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('gov.settings.passkeys.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: palette.text.secondary, mb: 2 }}>
            {t('gov.settings.passkeys.dialogBody')}
          </Typography>
          {enrollError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {enrollError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label={t('gov.settings.passkeys.labelField')}
            {...v.field('label')}
            placeholder={t('gov.settings.passkeys.labelPlaceholder')}
            value={label}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            onChange={(e) => setLabel(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={enroll.isPending}
            onClick={v.guard(() => void onEnroll())}
          >
            {enroll.isPending
              ? t('gov.settings.passkeys.enrolling')
              : t('gov.settings.passkeys.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revocation is destructive and irreversible, so it is confirmed. */}
      <Dialog open={!!pendingRevoke} onClose={() => setPendingRevoke(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('gov.settings.passkeys.revokeTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: palette.text.secondary }}>
            {t('gov.settings.passkeys.revokeBody', { label: pendingRevoke?.label ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRevoke(null)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={revoke.isPending}
            onClick={() => pendingRevoke && void onRevoke(pendingRevoke)}
          >
            {t('gov.settings.passkeys.revoke')}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
}
